// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\BrandCampaignWizard.jsx
// Название: Brand Campaign Wizard - View & Generate
// Версия: 2.0.0
// Дата: 30.12.20 25

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import CampaignStatsPanel from './CampaignStatsPanel';

const BrandCampaignWizard = () => {
  const { id: campaignId } = useParams();
  const navigate = useNavigate();

  // ============================================
  // STATE
  // ============================================
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [campaign, setCampaign] = useState(null);
  const [styles, setStyles] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [selectedGift, setSelectedGift] = useState(null);
  const [claims, setClaims] = useState([]);

  const [brandConfig, setBrandConfig] = useState({
    brandName: '',
    brandSlogan: '',
    brandUrl: 'dary.one',
    brandLogoUrl: '',
    aiStyle: 'premium',
    giftCount: 5,
    totalAmount: 10,
    lang: 'ru'
  });

  const [generationStatus, setGenerationStatus] = useState({
    total: 0,
    completed: 0,
    failed: 0,
    inProgress: false
  });

  // ============================================
  // LOAD DATA
  // ============================================
  useEffect(() => {
    loadAll();
  }, [campaignId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([loadCampaign(), loadStyles(), loadGifts()]);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaign = async () => {
    try {
      const response = await api.get(`/campaigns/${campaignId}`);
      const data = response.data || response;
      setCampaign(data);
      
      setBrandConfig(prev => ({
        ...prev,
        totalAmount: data.reward_amount || 10,
        giftCount: data.brand_config?.target_customers || 5,
        brandName: data.brand_config?.brandName || prev.brandName,
        brandSlogan: data.brand_config?.brandSlogan || prev.brandSlogan,
        aiStyle: data.brand_config?.aiStyle || prev.aiStyle
      }));
    } catch (err) {
      setError('Ошибка загрузки кампании');
    }
  };

  const loadStyles = async () => {
    try {
      const response = await api.get('/brand-gifts/styles');
      const data = response.data?.styles || response.styles || [];
      setStyles(data);
    } catch (err) {
      console.error('Error loading styles:', err);
    }
  };

  const loadGifts = async () => {
    try {
      const response = await api.get(`/brand-gifts/campaign/${campaignId}/gifts`);
      const data = response.data?.gifts || response.gifts || [];
      setGifts(data);
      
      // Если есть готовые подарки - сразу показываем результаты
      if (data.length > 0) {
        const readyGifts = data.filter(g => g.status === 'ready' && g.image_url);
        const claimedGifts = data.filter(g => g.status === 'claimed');
      if (readyGifts.length > 0 || claimedGifts.length > 0) {
          setStep(4); // Результаты
		  loadClaims();
        } else if (data.some(g => g.status === 'pending_generation')) {
          setStep(3); // Генерация
        }
      }
    } catch (err) {
      console.error('Error loading gifts:', err);
    }
  };
  
  const loadClaims = async () => {
    try {
      const response = await api.get(`/brand-gifts/campaign/${campaignId}/claims`);
      const data = response.data?.claims || response.claims || [];
      setClaims(data);
    } catch (err) {
      console.error('Error loading claims:', err);
    }
  };

  // ============================================
  // HANDLERS
  // ============================================
  const handleConfigChange = (field, value) => {
    setBrandConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateSeeds = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/brand-gifts/create-seeds', {
        campaignId: parseInt(campaignId),
        count: brandConfig.giftCount,
        totalAmount: brandConfig.totalAmount,
        aiStyle: brandConfig.aiStyle
      });
      
      console.log('[BrandWizard] Seeds created:', response);
      await loadGifts();
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка создания подарков');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setGenerationStatus({ total: gifts.length, completed: 0, failed: 0, inProgress: true });

    try {
      const response = await api.post(`/brand-gifts/campaign/${campaignId}/generate-all`, {
        aiStyle: brandConfig.aiStyle,
        brandName: brandConfig.brandName,
        brandUrl: brandConfig.brandUrl,
        brandLogoUrl: brandConfig.brandLogoUrl,
        campaignType: campaign?.occasion || 'gift',
        lang: brandConfig.lang
      });

      console.log('[BrandWizard] Generation result:', response);
      
      setGenerationStatus({
        total: response.data?.total || response.total || gifts.length,
        completed: response.data?.successful || response.successful || 0,
        failed: response.data?.failed || response.failed || 0,
        inProgress: false
      });

      await loadGifts();
      setStep(4);
	  loadClaims();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка генерации');
      setGenerationStatus(prev => ({ ...prev, inProgress: false }));
    } finally {
      setGenerating(false);
    }
  };

  // ============================================
  // DOWNLOAD HANDLERS
  // ============================================
  const downloadImage = async (gift) => {
    try {
      const link = document.createElement('a');
      link.href = gift.image_url;
      link.download = `DARY_Gift.png`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const downloadAllZip = async () => {
    try {
      window.open(`https://qr-money.onrender.com/api/brand-gifts/campaign/${campaignId}/download-zip?token=${localStorage.getItem('authToken')}`, '_blank');
    } catch (err) {
      alert('Ошибка скачивания ZIP');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    alert(`Код скопирован: ${code}`);
  };

  // ============================================
  // STYLES
  // ============================================
  const s = {
    container: { padding: 24, maxWidth: 1200, margin: '0 auto' },
    header: { marginBottom: 24 },
    title: { fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 },
    subtitle: { color: '#6b7280', marginTop: 4 },
    badge: { display: 'inline-block', background: '#8B5CF6', color: 'white', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, marginRight: 8 },
    
    steps: { display: 'flex', gap: 8, marginBottom: 32 },
    stepItem: { flex: 1, padding: '12px 16px', background: '#f3f4f6', borderRadius: 12, textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s' },
    stepItemActive: { background: '#8B5CF6', color: 'white' },
    stepItemDone: { background: '#10B981', color: 'white' },
    stepNum: { fontSize: 18, fontWeight: 700 },
    stepLabel: { fontSize: 12, marginTop: 4 },

    section: { background: 'white', borderRadius: 16, padding: 24, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    sectionTitle: { fontSize: 18, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 },

    row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
    field: { marginBottom: 16 },
    label: { display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 },
    input: { width: '100%', padding: '12px 14px', border: '2px solid #e5e7eb', borderRadius: 10, fontSize: 14, boxSizing: 'border-box' },

    styleGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 },
    styleCard: { padding: 16, border: '2px solid #e5e7eb', borderRadius: 12, cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s' },
    styleCardSelected: { borderColor: '#8B5CF6', background: '#faf5ff' },
    styleName: { fontSize: 13, fontWeight: 500, marginTop: 8 },

    giftGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 },
    giftCard: { background: '#f9fafb', borderRadius: 16, overflow: 'hidden', border: '2px solid #e5e7eb', transition: 'all 0.2s', cursor: 'pointer' },
    giftCardHover: { borderColor: '#8B5CF6', transform: 'translateY(-2px)' },
    giftImage: { width: '100%', aspectRatio: '1', objectFit: 'cover' },
    giftInfo: { padding: 16 },
    giftCode: { fontSize: 16, fontWeight: 700, color: '#111827', fontFamily: 'monospace', letterSpacing: 1 },
    giftAmount: { fontSize: 14, color: '#10B981', fontWeight: 600, marginTop: 4 },
    giftStatus: { fontSize: 11, padding: '4px 10px', borderRadius: 10, display: 'inline-block', marginTop: 8 },
    giftActions: { display: 'flex', gap: 8, marginTop: 12 },
    giftBtn: { flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 },
    giftBtnPrimary: { background: '#8B5CF6', color: 'white' },
    giftBtnSecondary: { background: '#e5e7eb', color: '#374151' },

    actions: { display: 'flex', justifyContent: 'space-between', marginTop: 24, gap: 12 },
    btn: { padding: '14px 28px', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer', border: 'none' },
    btnPrimary: { background: '#10B981', color: 'white' },
    btnSecondary: { background: '#f3f4f6', color: '#374151' },
    btnPurple: { background: '#8B5CF6', color: 'white' },
    btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },

    progress: { textAlign: 'center', padding: 40 },
    progressTitle: { fontSize: 20, fontWeight: 600, marginBottom: 16 },
    progressBar: { height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden', marginBottom: 16 },
    progressFill: { height: '100%', background: '#10B981', transition: 'width 0.3s' },
    progressText: { fontSize: 14, color: '#6b7280' },

    error: { background: '#fef2f2', color: '#dc2626', padding: 16, borderRadius: 12, marginBottom: 16 },

    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
    statCard: { background: '#f9fafb', borderRadius: 12, padding: 16, textAlign: 'center' },
    statValue: { fontSize: 24, fontWeight: 700, color: '#111827' },
    statLabel: { fontSize: 12, color: '#6b7280', marginTop: 4 },

    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { background: 'white', borderRadius: 16, padding: 24, maxWidth: 600, width: '90%', maxHeight: '90vh', overflow: 'auto' },
    modalImage: { width: '100%', borderRadius: 12 },
    modalClose: { position: 'absolute', top: 16, right: 16, background: 'white', border: 'none', borderRadius: '50%', width: 40, height: 40, cursor: 'pointer', fontSize: 20 }
  };

  // ============================================
  // RENDER HELPERS
  // ============================================
  const renderSteps = () => (
    <div style={s.steps}>
      {[
        { num: 1, label: 'Бренд' },
        { num: 2, label: 'Стиль AI' },
        { num: 3, label: 'Генерация' },
        { num: 4, label: 'Результаты' }
      ].map(st => (
        <div
          key={st.num}
          style={{
            ...s.stepItem,
            ...(step === st.num ? s.stepItemActive : {}),
            ...(step > st.num ? s.stepItemDone : {})
          }}
          onClick={() => st.num <= step && setStep(st.num)}
        >
          <div style={s.stepNum}>{step > st.num ? '✓' : st.num}</div>
          <div style={s.stepLabel}>{st.label}</div>
        </div>
      ))}
    </div>
  );

  const getStyleEmoji = (styleId) => {
    const emojis = {
      premium: '💎', festive: '🎉', corporate: '🏢', tech: '🚀',
      minimal: '⬜', gaming: '🎮', birthday: '🎂', christmas: '🎄',
      newyear: '🎆', valentine: '❤️', easter: '🐣', halloween: '🎃',
      referral: '🔗', welcome: '👋', thankyou: '🙏', loyalty: '👑',
      promo: '🎯', seasonal: '🍂', fun: '🌈', music: '🎵',
      travel: '✈️', food: '🍽️', coffee: '☕'
    };
    return emojis[styleId] || '🎁';
  };

  // ============================================
  // STEP 1: Brand Config
  // ============================================
  const renderStep1 = () => (
    <div style={s.section}>
      <h3 style={s.sectionTitle}>🏢 Настройка бренда</h3>
      
      <div style={s.row}>
        <div style={s.field}>
          <label style={s.label}>Название бренда *</label>
          <input
            style={s.input}
            placeholder="Например: DARY"
            value={brandConfig.brandName}
            onChange={(e) => handleConfigChange('brandName', e.target.value)}
          />
        </div>
        <div style={s.field}>
          <label style={s.label}>Слоган</label>
          <input
            style={s.input}
            placeholder="Ваш слоган..."
            value={brandConfig.brandSlogan}
            onChange={(e) => handleConfigChange('brandSlogan', e.target.value)}
          />
        </div>
      </div>

      <div style={s.row}>
        <div style={s.field}>
          <label style={s.label}>URL бренда</label>
          <input
            style={s.input}
            placeholder="example.com"
            value={brandConfig.brandUrl}
            onChange={(e) => handleConfigChange('brandUrl', e.target.value)}
          />
        </div>
        <div style={s.field}>
          <label style={s.label}>Язык</label>
          <select
            style={s.input}
            value={brandConfig.lang}
            onChange={(e) => handleConfigChange('lang', e.target.value)}
          >
            <option value="ru">🇷🇺 Русский</option>
            <option value="en">🇬🇧 English</option>
            <option value="fr">🇫🇷 Français</option>
            <option value="de">🇩🇪 Deutsch</option>
            <option value="uk">🇺🇦 Українська</option>
          </select>
        </div>
      </div>

      <div style={s.row}>
        <div style={s.field}>
          <label style={s.label}>Количество подарков</label>
          <input
            style={s.input}
            type="number"
            min="1"
            max="100"
            value={brandConfig.giftCount}
            onChange={(e) => handleConfigChange('giftCount', parseInt(e.target.value) || 1)}
          />
        </div>
        <div style={s.field}>
          <label style={s.label}>Сумма каждого подарка (DAR)</label>
          <input
            style={s.input}
            type="number"
            min="1"
            value={brandConfig.totalAmount}
            onChange={(e) => handleConfigChange('totalAmount', parseFloat(e.target.value) || 1)}
          />
        </div>
      </div>

      <div style={s.actions}>
        <button style={{ ...s.btn, ...s.btnSecondary }} onClick={() => navigate('/business/brand/campaigns')}>
          ← К списку
        </button>
        <button
          style={{ ...s.btn, ...s.btnPrimary, ...(!brandConfig.brandName ? s.btnDisabled : {}) }}
          onClick={() => setStep(2)}
          disabled={!brandConfig.brandName}
        >
          Далее: Выбор стиля →
        </button>
      </div>
    </div>
  );

  // ============================================
  // STEP 2: Style Selection
  // ============================================
  const renderStep2 = () => (
    <div style={s.section}>
      <h3 style={s.sectionTitle}>🎨 Выберите AI стиль</h3>
      
      <div style={s.styleGrid}>
        {styles.map(st => (
          <div
            key={st.id}
            style={{
              ...s.styleCard,
              ...(brandConfig.aiStyle === st.id ? s.styleCardSelected : {})
            }}
            onClick={() => handleConfigChange('aiStyle', st.id)}
          >
            <div style={{ fontSize: 28 }}>{getStyleEmoji(st.id)}</div>
            <div style={s.styleName}>{st.name?.ru || st.id}</div>
          </div>
        ))}
      </div>

      <div style={s.actions}>
        <button style={{ ...s.btn, ...s.btnSecondary }} onClick={() => setStep(1)}>
          ← Назад
        </button>
        <button
          style={{ ...s.btn, ...s.btnPrimary, ...(loading ? s.btnDisabled : {}) }}
          onClick={handleCreateSeeds}
          disabled={loading}
        >
          {loading ? '⏳ Создание...' : `Создать ${brandConfig.giftCount} подарков →`}
        </button>
      </div>
    </div>
  );

  // ============================================
  // STEP 3: Generation
  // ============================================
  const renderStep3 = () => {
    const pendingGifts = gifts.filter(g => g.status === 'pending_generation' || !g.image_url);
    
    return (
      <div style={s.section}>
        <h3 style={s.sectionTitle}>🤖 AI Генерация изображений</h3>
        
        {generating ? (
          <div style={s.progress}>
            <div style={s.progressTitle}>⏳ Генерируем изображения...</div>
            <div style={s.progressBar}>
              <div style={{ ...s.progressFill, width: `${(generationStatus.completed / generationStatus.total) * 100}%` }} />
            </div>
            <div style={s.progressText}>
              {generationStatus.completed} / {generationStatus.total} готово
            </div>
            <p style={{ marginTop: 16, color: '#6b7280' }}>
              Это может занять несколько минут...
            </p>
          </div>
        ) : (
          <>
            <div style={s.statsGrid}>
              <div style={s.statCard}>
                <div style={s.statValue}>{gifts.length}</div>
                <div style={s.statLabel}>Всего подарков</div>
              </div>
              <div style={s.statCard}>
                <div style={s.statValue}>{pendingGifts.length}</div>
                <div style={s.statLabel}>Ожидают генерации</div>
              </div>
              <div style={s.statCard}>
                <div style={s.statValue}>{brandConfig.aiStyle}</div>
                <div style={s.statLabel}>Стиль</div>
              </div>
              <div style={s.statCard}>
                <div style={s.statValue}>~${(pendingGifts.length * 0.04).toFixed(2)}</div>
                <div style={s.statLabel}>Стоимость</div>
              </div>
            </div>
            
            <div style={s.actions}>
              <button style={{ ...s.btn, ...s.btnSecondary }} onClick={() => setStep(2)}>
                ← Назад
              </button>
              <button
                style={{ ...s.btn, ...s.btnPrimary }}
                onClick={handleGenerate}
                disabled={pendingGifts.length === 0}
              >
                🚀 Запустить генерацию ({pendingGifts.length} шт)
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  // ============================================
  // STEP 4: Results
  // ============================================
  const renderStep4 = () => {
    const readyGifts = gifts.filter(g => g.status === 'ready' && g.image_url);
    const failedGifts = gifts.filter(g => g.status === 'failed' || (g.status !== 'ready' && !g.image_url));
    
    return (
      <>
        <CampaignStatsPanel campaignId={campaignId} />
        
        <div style={s.section}>
          <h3 style={s.sectionTitle}>🎁 Подарки кампании</h3>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
            <button 
              style={{ ...s.btn, ...s.btnPurple }}
              onClick={downloadAllZip}
              disabled={readyGifts.length === 0}
            >
              📦 Скачать все ZIP ({readyGifts.length})
            </button>
            <button style={{ ...s.btn, ...s.btnSecondary }}>
              📧 Отправить по Email
            </button>
            {failedGifts.length > 0 && (
              <button 
                style={{ ...s.btn, ...s.btnSecondary }}
                onClick={() => setStep(3)}
              >
                🔄 Перегенерировать ({failedGifts.length})
              </button>
            )}
          </div>

          {/* Gifts Grid */}
          <div style={s.giftGrid}>
            {gifts.map(gift => (
              <div 
                key={gift.id} 
                style={s.giftCard}
                onClick={() => gift.image_url && setSelectedGift(gift)}
              >
                {gift.image_url ? (
                  <img src={gift.image_url} alt={gift.short_code} style={s.giftImage} />
                ) : (
                  <div style={{ ...s.giftImage, background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 48 }}>⏳</span>
                  </div>
                )}
                <div style={s.giftInfo}>
                  <div style={s.giftCode}>{gift.short_code || '----'}</div>
                  <div style={s.giftAmount}>{parseFloat(gift.total_amount).toFixed(2)} DAR</div>
                  <span style={{
                    ...s.giftStatus,
                    background: gift.status === 'ready' ? '#dcfce7' : 
                               gift.status === 'claimed' ? '#fef3c7' : '#fee2e2',
                    color: gift.status === 'ready' ? '#16a34a' : 
                           gift.status === 'claimed' ? '#d97706' : '#dc2626'
                  }}>
                    {gift.status === 'ready' ? '✅ Готов' : 
                     gift.status === 'claimed' ? '🎉 Активирован' : 
                     '❌ ' + gift.status}
                  </span>
                  
                  {gift.image_url && (
                    <div style={s.giftActions}>
                      <button 
                        style={{ ...s.giftBtn, ...s.giftBtnPrimary }}
                        onClick={(e) => { e.stopPropagation(); downloadImage(gift); }}
                      >
                        ⬇️ Скачать
                      </button>
                      <button 
                        style={{ ...s.giftBtn, ...s.giftBtnSecondary }}
                        onClick={(e) => { e.stopPropagation(); copyCode(gift.short_code); }}
                      >
                        📋 Код
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={s.actions}>
            <button style={{ ...s.btn, ...s.btnSecondary }} onClick={() => navigate('/business/brand/campaigns')}>
              ← К списку кампаний
            </button>
          </div>
        </div>
      </>
    );
  };
  
  // ============================================
  // MODAL - Gift Preview
  // ============================================
  const renderModal = () => {
    if (!selectedGift) return null;
    
    return (
      <div style={s.modal} onClick={() => setSelectedGift(null)}>
        <div style={s.modalContent} onClick={(e) => e.stopPropagation()}>
          <img src={selectedGift.image_url} alt={selectedGift.short_code} style={s.modalImage} />
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'monospace' }}>
              {selectedGift.short_code}
            </div>
            <div style={{ fontSize: 18, color: '#10B981', marginTop: 8 }}>
              {parseFloat(selectedGift.total_amount).toFixed(2)} DAR
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 16 }}>
              <button 
                style={{ ...s.btn, ...s.btnPrimary }}
                onClick={() => downloadImage(selectedGift)}
              >
                ⬇️ Скачать PNG
              </button>
              <button 
                style={{ ...s.btn, ...s.btnSecondary }}
                onClick={() => copyCode(selectedGift.short_code)}
              >
                📋 Копировать код
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  if (loading && !campaign) {
    return (
      <div style={s.container}>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
          <p>Загрузка кампании...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={s.container}>
      <div style={s.header}>
        <span style={s.badge}>BRAND</span>
        <h1 style={s.title}>{campaign?.name || 'Brand кампания'}</h1>
        <p style={s.subtitle}>
          {gifts.length > 0 
            ? `${gifts.filter(g => g.status === 'ready').length} из ${gifts.length} подарков готовы`
            : 'Создание брендированных AI-подарков'}
        </p>
      </div>

      {renderSteps()}
      {error && <div style={s.error}>❌ {error}</div>}

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
      
      {renderModal()}
    </div>
  );
};

export default BrandCampaignWizard;
